/**
 * Pitch (Setback) — server-authoritative rules engine (4 players in two partnerships).
 *
 * Rules implemented (toggles live in {@link PitchConfig}):
 *  - 4 players in two partnerships: seats 0 & 2 (team 0) against seats 1 & 3 (team 1). Standard 52-card deck, six
 *    cards each, dealt all at once.
 *  - Bidding, once around starting left of the dealer: pass or bid 2, 3 or 4 (higher than the bid so far). The dealer
 *    speaks last and may take the bid by matching it; if everyone else passed the dealer is stuck with the minimum bid
 *    of 2 and cannot pass. "Smudge" (a bid of 5) is not played.
 *  - The high bidder leads the first card ("pitches"); the suit of that card is trump for the hand. Players must
 *    follow the led suit or play a trump (a trump may be played at any time); a player void in the led suit may play
 *    any card. When trump is led, a player holding a trump must play one. Highest trump wins the trick, otherwise the
 *    highest card of the led suit (ace high). The winner leads the next of six tricks.
 *  - There is no draw or discard phase after the bid (simplification): the six dealt cards are the hand.
 *  - Four points are on offer every hand: High (the highest trump dealt) and Low (the lowest trump dealt) go to the
 *    partnership that was dealt them, whichever side captures the card; Jack (the jack of trump, if dealt) goes to the
 *    partnership that wins the trick containing it; Game goes to the partnership with more card points taken in tricks
 *    (A 4, K 3, Q 2, J 1, 10 10) — a tie scores no Game point.
 *  - The bidding partnership scores what it took if that reaches its bid, otherwise it is set back by the bid (its
 *    score drops). The other partnership always scores what it took.
 *  - The match ends when a partnership reaches `targetScore` at the end of a hand; if both do the bidders win. `maxHands`
 *    is a hard cap so a match always terminates (the higher score wins). Partners share a place, so payouts are split
 *    between the two partners.
 */
import { buildDeck, parseCard, removeCards, sortHand, type CardId, type Suit } from '../../core/cards.js';
import { checkInt, mergeKnown } from '../../core/config.js';
import { shuffle, type Rng } from '../../core/rng.js';
import {
  assertEngine,
  EngineError,
  isPlainObject,
  type EngineEvent,
  type GameEngine,
  type Standing,
} from '../../core/types.js';

export interface PitchConfig {
  /** Match ends when a partnership reaches this score at the end of a hand. */
  targetScore: number;
  /** Hard cap on hands so a match always terminates. */
  maxHands: number;
  handEndDelayMs: number;
  trickEndDelayMs: number;
}

export type PitchPhase = 'BIDDING' | 'PLAYING' | 'TRICK_END' | 'HAND_END' | 'MATCH_END';

export interface PitchTrickPlay {
  seat: number;
  card: CardId;
}

export interface PitchCompletedTrick {
  leader: number;
  plays: PitchTrickPlay[];
  winner: number;
}

/** One bidding call, `bid: null` = pass. */
export interface PitchBid {
  seat: number;
  bid: number | null;
}

/** Scoring breakdown of a finished hand (public). */
export interface PitchHandResult {
  bidder: number;
  bid: number;
  trump: Suit;
  /** Team that won each point, or null when it was not scored (Jack not dealt, Game tied). */
  high: number | null;
  low: number | null;
  jack: number | null;
  game: number | null;
  /** Game-point card counts per team. */
  gameCards: number[];
  /** Points each team took this hand, before the bid is applied. */
  earned: number[];
  /** Whether the bidding team reached its bid. */
  made: boolean;
  /** Points added to (or taken from) each team's match score. */
  points: number[];
}

export interface PitchState {
  config: PitchConfig;
  players: string[];
  hand: number;
  dealer: number;
  phase: PitchPhase;
  hands: CardId[][];
  /** Cards as dealt, kept to award High and Low (hidden from everyone). */
  dealt: CardId[][];
  bids: PitchBid[];
  /** Highest bid so far (0 = none) and who made it. */
  highBid: number;
  bidder: number | null;
  trump: Suit | null;
  tricksWon: number[];
  turn: number;
  trick: PitchTrickPlay[];
  tricks: PitchCompletedTrick[];
  /** Team scores, index = team (seat % 2). */
  teamScores: number[];
  handResults: PitchHandResult[];
  /** Set at match end when both teams reached the target in one hand: the bidders win. */
  winnerTeam: number | null;
}

export type PitchAction = { type: 'bid'; amount: number } | { type: 'pass' } | { type: 'play'; card: CardId };

export type PitchLegal =
  | { kind: 'bid'; amounts: number[]; canPass: boolean }
  | { kind: 'play'; cards: CardId[] }
  | { kind: 'none' };

export interface PitchView {
  gameType: 'PITCH';
  config: PitchConfig;
  seat: number | null;
  phase: PitchPhase;
  round: number;
  dealer: number;
  turn: number;
  hand: CardId[];
  handCounts: number[];
  bids: PitchBid[];
  highBid: number;
  bidder: number | null;
  trump: Suit | null;
  tricksWon: number[];
  trick: PitchTrickPlay[];
  lastTrick: PitchCompletedTrick | null;
  tricks: PitchCompletedTrick[];
  teamScores: number[];
  handResults: PitchHandResult[];
  /** Score of each seat's team (so generic score UIs work). */
  totals: number[];
}

const DEFAULTS: PitchConfig = {
  targetScore: 11,
  maxHands: 20,
  handEndDelayMs: 5000,
  trickEndDelayMs: 900,
};

const SEATS = 4;
const TRICKS = 6;
export const PITCH_MIN_BID = 2;
export const PITCH_MAX_BID = 4;

/** Game-point value of a card taken in tricks. */
const GAME_POINTS: Record<number, number> = { 14: 4, 13: 3, 12: 2, 11: 1, 10: 10 };

export const pitchTeamOf = (seat: number): number => seat % 2;
export const pitchPartnerOf = (seat: number): number => (seat + 2) % SEATS;
export const pitchGameValue = (card: CardId): number => GAME_POINTS[parseCard(card).rank] ?? 0;

/** Winner of a trick: highest trump, otherwise the highest card of the led suit. */
export function pitchTrickWinner(plays: readonly PitchTrickPlay[], trump: Suit): number {
  const lead = parseCard(plays[0]!.card).suit;
  let best = plays[0]!;
  for (const p of plays.slice(1)) {
    const c = parseCard(p.card);
    const b = parseCard(best.card);
    if (c.suit === trump) {
      if (b.suit !== trump || c.rank > b.rank) best = p;
    } else if (c.suit === lead && b.suit === lead && c.rank > b.rank) best = p;
  }
  return best.seat;
}

/** Cards a seat may play: the led suit or any trump; anything when void in the led suit. */
export function pitchLegalCards(hand: readonly CardId[], trick: readonly PitchTrickPlay[], trump: Suit | null): CardId[] {
  if (trick.length === 0 || trump === null) return [...hand];
  const lead = parseCard(trick[0]!.card).suit;
  const playable = hand.filter((c) => {
    const s = parseCard(c).suit;
    return s === lead || s === trump;
  });
  return playable.length > 0 ? playable : [...hand];
}

/** Bids a seat may make now, and whether it may pass. */
export function pitchBidOptions(state: Pick<PitchState, 'bids' | 'highBid' | 'dealer'>, seat: number): { amounts: number[]; canPass: boolean } {
  const isDealer = seat === state.dealer;
  const from = isDealer ? Math.max(PITCH_MIN_BID, state.highBid) : Math.max(PITCH_MIN_BID, state.highBid + 1);
  const amounts: number[] = [];
  for (let a = from; a <= PITCH_MAX_BID; a++) amounts.push(a);
  // The dealer is stuck with the minimum bid when nobody else bid.
  const canPass = !(isDealer && state.highBid === 0);
  return { amounts, canPass };
}

/** Scores a finished hand from the deal and the tricks. */
export function pitchScoreHand(args: {
  dealt: readonly (readonly CardId[])[];
  tricks: readonly PitchCompletedTrick[];
  trump: Suit;
  bidder: number;
  bid: number;
}): Omit<PitchHandResult, 'bidder' | 'bid' | 'trump'> {
  const { dealt, tricks, trump } = args;
  const trumpDealt: { card: CardId; seat: number; rank: number }[] = [];
  dealt.forEach((cards, seat) => {
    for (const c of cards) {
      const p = parseCard(c);
      if (p.suit === trump) trumpDealt.push({ card: c, seat, rank: p.rank });
    }
  });
  trumpDealt.sort((a, b) => a.rank - b.rank);
  const high = trumpDealt.length > 0 ? pitchTeamOf(trumpDealt.at(-1)!.seat) : null;
  const low = trumpDealt.length > 0 ? pitchTeamOf(trumpDealt[0]!.seat) : null;
  const jackCard = `J${trump}`;
  let jack: number | null = null;
  const gameCards = [0, 0];
  for (const t of tricks) {
    const team = pitchTeamOf(t.winner);
    for (const p of t.plays) {
      if (p.card === jackCard) jack = team;
      gameCards[team]! += pitchGameValue(p.card);
    }
  }
  const game = gameCards[0]! === gameCards[1]! ? null : gameCards[0]! > gameCards[1]! ? 0 : 1;
  const earned = [0, 0];
  for (const w of [high, low, jack, game]) if (w !== null) earned[w]!++;
  const bidTeam = pitchTeamOf(args.bidder);
  const made = earned[bidTeam]! >= args.bid;
  const points = [...earned];
  if (!made) points[bidTeam] = -args.bid;
  return { high, low, jack, game, gameCards, earned, made, points };
}

function deal(state: PitchState, rng: Rng, events: EngineEvent[]): void {
  const deck = shuffle(buildDeck(), rng);
  const hands: CardId[][] = [[], [], [], []];
  for (let i = 0; i < SEATS * TRICKS; i++) hands[(state.dealer + 1 + (i % SEATS)) % SEATS]!.push(deck[i]!);
  state.hands = hands.map((h) => sortHand(h));
  state.dealt = state.hands.map((h) => [...h]);
  state.bids = [];
  state.highBid = 0;
  state.bidder = null;
  state.trump = null;
  state.tricksWon = [0, 0, 0, 0];
  state.trick = [];
  state.tricks = [];
  state.phase = 'BIDDING';
  state.turn = (state.dealer + 1) % SEATS;
  events.push({ type: 'dealt', hand: state.hand, dealer: state.dealer, counts: state.hands.map((h) => h.length) });
}

/** Partners share a place: the leading team is 1st, the other 2nd (both 1st on a tie). */
function teamStandings(state: PitchState): Standing[] {
  const [a, b] = state.teamScores as [number, number];
  return [0, 1, 2, 3].map((seat) => {
    const team = pitchTeamOf(seat);
    const mine = team === 0 ? a : b;
    const theirs = team === 0 ? b : a;
    const wins = state.winnerTeam === null ? mine >= theirs : state.winnerTeam === team;
    return { seat, score: mine, place: wins ? 1 : 2 };
  });
}

export const pitchEngine: GameEngine<PitchConfig, PitchState, PitchAction, PitchView, PitchLegal> = {
  gameType: 'PITCH',
  kind: 'match',

  defaultConfig: () => ({ ...DEFAULTS }),

  normalizeConfig(input) {
    const c = mergeKnown(DEFAULTS, input);
    checkInt('targetScore', c.targetScore, 1, 100);
    checkInt('maxHands', c.maxHands, 1, 60);
    checkInt('handEndDelayMs', c.handEndDelayMs, 0, 60000);
    checkInt('trickEndDelayMs', c.trickEndDelayMs, 0, 10000);
    return c;
  },

  seatLimits: () => ({ min: SEATS, max: SEATS }),

  createState(config, seats, rng) {
    assertEngine(seats.length === SEATS, 'INVALID_CONFIG', 'Pitch needs exactly 4 seats');
    const players = Array<string>(SEATS).fill('');
    for (const s of seats) {
      assertEngine(s.seat >= 0 && s.seat < SEATS && !players[s.seat], 'SEAT_TAKEN', `Bad seat ${s.seat}`);
      players[s.seat] = s.playerId;
    }
    const state: PitchState = {
      config,
      players,
      hand: 1,
      dealer: rng.int(SEATS),
      phase: 'BIDDING',
      hands: [],
      dealt: [],
      bids: [],
      highBid: 0,
      bidder: null,
      trump: null,
      tricksWon: [],
      turn: 0,
      trick: [],
      tricks: [],
      teamScores: [0, 0],
      handResults: [],
      winnerTeam: null,
    };
    deal(state, rng, []);
    return state;
  },

  parseAction(raw) {
    if (!isPlainObject(raw)) throw new EngineError('INVALID_ACTION', 'Action must be an object');
    if (raw.type === 'pass') return { type: 'pass' };
    if (raw.type === 'bid' && typeof raw.amount === 'number' && Number.isInteger(raw.amount)) return { type: 'bid', amount: raw.amount };
    if (raw.type === 'play' && typeof raw.card === 'string' && raw.card.length <= 4) return { type: 'play', card: raw.card };
    throw new EngineError('INVALID_ACTION', 'Unknown Pitch action');
  },

  applyAction(state, seat, action) {
    assertEngine(state.phase !== 'MATCH_END', 'GAME_OVER', 'Match is over');
    assertEngine(seat === state.turn, 'NOT_YOUR_TURN', 'Not your turn');
    const events: EngineEvent[] = [];

    if (state.phase === 'BIDDING') {
      assertEngine(action.type === 'bid' || action.type === 'pass', 'INVALID_PHASE', 'Bidding is in progress');
      const opts = pitchBidOptions(state, seat);
      if (action.type === 'bid') {
        assertEngine(opts.amounts.includes(action.amount), 'ILLEGAL_MOVE', 'That bid is not allowed');
        state.bids.push({ seat, bid: action.amount });
        state.highBid = action.amount;
        state.bidder = seat;
        events.push({ type: 'bid', seat, bid: action.amount });
      } else {
        assertEngine(opts.canPass, 'ILLEGAL_MOVE', 'The dealer must bid when nobody else has');
        state.bids.push({ seat, bid: null });
        events.push({ type: 'bid', seat, bid: null });
      }
      if (state.bids.length < SEATS) {
        state.turn = (seat + 1) % SEATS;
        return events;
      }
      // Bidding is over: the high bidder pitches the first card.
      state.phase = 'PLAYING';
      state.turn = state.bidder!;
      events.push({ type: 'biddingEnd', bidder: state.bidder, bid: state.highBid });
      return events;
    }

    assertEngine(state.phase === 'PLAYING' && action.type === 'play', 'INVALID_PHASE', 'You must play a card');
    const hand = state.hands[seat]!;
    assertEngine(hand.includes(action.card), 'INVALID_CARD', 'Card not in hand');
    assertEngine(pitchLegalCards(hand, state.trick, state.trump).includes(action.card), 'ILLEGAL_MOVE', 'You must follow suit or play a trump');
    removeCards(hand, [action.card]);
    if (state.trump === null) {
      state.trump = parseCard(action.card).suit;
      events.push({ type: 'trumpChosen', seat, suit: state.trump });
    }
    state.trick.push({ seat, card: action.card });
    events.push({ type: 'cardPlayed', seat, card: action.card });
    if (state.trick.length < SEATS) {
      state.turn = (seat + 1) % SEATS;
      return events;
    }
    const winner = pitchTrickWinner(state.trick, state.trump);
    state.tricksWon[winner]!++;
    state.tricks.push({ leader: state.trick[0]!.seat, plays: state.trick, winner });
    events.push({ type: 'trickWon', seat: winner, trickNo: state.tricks.length });
    state.phase = 'TRICK_END';
    state.turn = winner;
    return events;
  },

  getActors(state) {
    return state.phase === 'BIDDING' || state.phase === 'PLAYING' ? [state.turn] : [];
  },

  getLegalActions(state, seat) {
    if (seat !== state.turn) return { kind: 'none' };
    if (state.phase === 'BIDDING') {
      const { amounts, canPass } = pitchBidOptions(state, seat);
      return { kind: 'bid', amounts, canPass };
    }
    if (state.phase !== 'PLAYING') return { kind: 'none' };
    return { kind: 'play', cards: pitchLegalCards(state.hands[seat]!, state.trick, state.trump) };
  },

  getTimeoutAction(state, seat) {
    if (state.phase === 'BIDDING') {
      const { amounts, canPass } = pitchBidOptions(state, seat);
      return canPass ? { type: 'pass' } : { type: 'bid', amount: amounts[0]! };
    }
    const legal = pitchLegalCards(state.hands[seat]!, state.trick, state.trump);
    // Cheapest legal card by game value, then rank: rarely wastes a winner and is never illegal.
    const lowest = [...legal].sort((a, b) => pitchGameValue(a) - pitchGameValue(b) || parseCard(a).rank - parseCard(b).rank)[0]!;
    return { type: 'play', card: lowest };
  },

  getView(state, seat) {
    return {
      gameType: 'PITCH',
      config: state.config,
      seat,
      phase: state.phase,
      round: state.hand,
      dealer: state.dealer,
      turn: state.turn,
      hand: seat === null ? [] : [...state.hands[seat]!],
      handCounts: state.hands.map((h) => h.length),
      bids: state.bids.map((b) => ({ ...b })),
      highBid: state.highBid,
      bidder: state.bidder,
      trump: state.trump,
      tricksWon: [...state.tricksWon],
      trick: state.phase === 'TRICK_END' ? [...(state.tricks.at(-1)?.plays ?? [])] : [...state.trick],
      lastTrick: state.tricks.at(-1) ?? null,
      tricks: state.tricks.map((t) => ({ ...t, plays: [...t.plays] })),
      teamScores: [...state.teamScores],
      handResults: state.handResults.map((r) => ({ ...r, gameCards: [...r.gameCards], earned: [...r.earned], points: [...r.points] })),
      totals: [0, 1, 2, 3].map((s) => state.teamScores[pitchTeamOf(s)]!),
    };
  },

  getSystemStep(state) {
    if (state.phase === 'TRICK_END') return { kind: 'collectTrick', delayMs: state.config.trickEndDelayMs };
    if (state.phase === 'HAND_END') return { kind: 'nextHand', delayMs: state.config.handEndDelayMs };
    return null;
  },

  runSystemStep(state, rng) {
    const events: EngineEvent[] = [];
    if (state.phase === 'TRICK_END') {
      state.trick = [];
      if (state.tricks.length < TRICKS) {
        state.phase = 'PLAYING';
        events.push({ type: 'trickCollected', leader: state.turn });
        return events;
      }
      const scored = pitchScoreHand({ dealt: state.dealt, tricks: state.tricks, trump: state.trump!, bidder: state.bidder!, bid: state.highBid });
      scored.points.forEach((p, team) => {
        state.teamScores[team]! += p;
      });
      const result: PitchHandResult = { bidder: state.bidder!, bid: state.highBid, trump: state.trump!, ...scored };
      state.handResults.push(result);
      events.push({ type: 'handEnd', hand: state.hand, result, teamScores: [...state.teamScores], tricksWon: [...state.tricksWon] });
      const cfg = state.config;
      const [a, b] = state.teamScores as [number, number];
      const bidTeam = pitchTeamOf(state.bidder!);
      const reached = a >= cfg.targetScore || b >= cfg.targetScore;
      const capped = state.hand >= cfg.maxHands;
      if (reached || capped) {
        // Both teams over the line: the bidders win; otherwise the higher score wins.
        if (a >= cfg.targetScore && b >= cfg.targetScore) state.winnerTeam = bidTeam;
        state.phase = 'MATCH_END';
        events.push({ type: 'matchEnd', standings: teamStandings(state) });
      } else {
        state.phase = 'HAND_END';
      }
      return events;
    }
    if (state.phase === 'HAND_END') {
      state.hand++;
      state.dealer = (state.dealer + 1) % SEATS;
      deal(state, rng, events);
      return events;
    }
    return events;
  },

  isFinished: (state) => state.phase === 'MATCH_END',

  getStandings(state): Standing[] {
    return teamStandings(state);
  },

  getRoundsCompleted: (state) => state.handResults.length,
};
